import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Tag, Table, Tabs } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_APPOINTMENTS } from 'gql/appointments/queries';
import {
  Appointments as AppointmentsType,
  Appointments_appointmentsForDoctor_data_results as AppointmentsForDoctorType,
  Appointments_appointmentsForDoctor_data_results_patient as PatientType
} from 'gql/appointments/__generated__/Appointments';
import { APPOINTMENT_STATUSES } from 'constants/appointments';
import { AppointmentById_appointment_data_dates as DateType } from 'gql/appointments/__generated__/AppointmentById';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import { ColumnProps } from 'antd/lib/table';

import { PageWrapper } from 'components/ui';
import { getIdStart } from 'utils/string';
import { showErrorMessage } from 'utils/showErrorMessage';
import { CreateInvoiceModal, HowItWorksLink } from 'components/shared';
import DownloadInvoice from './DownloadInvoice';

const PAGE_LIMIT = 10;

enum TabsValues {
  all = 'all',
  invoiced = 'invoiced',
  notInvoiced = 'notInvoiced'
}

const Invoices: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') || 0);
  const tab = searchParams.get('tab');

  useEffect(() => {
    if (!tab) {
      searchParams.set('tab', TabsValues.notInvoiced);
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, tab]);

  const isInvoiced = useMemo(() => {
    switch (tab) {
      case TabsValues.invoiced:
        return '1';
      case TabsValues.notInvoiced:
        return '0';
      default:
        return undefined;
    }
  }, [tab]);

  const { data, loading } = useQuery<AppointmentsType>(GET_APPOINTMENTS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      query: {
        status: APPOINTMENT_STATUSES.completed,
        invoice: isInvoiced,
        pagination: {
          page,
          limit: PAGE_LIMIT
        }
      }
    },
    onError: err => showErrorMessage(err)
  });

  const onTabChange = (key: string) => {
    searchParams.set('tab', key);
    searchParams.set('page', '0');
    setSearchParams(searchParams);
  };

  const columns: ColumnProps<AppointmentsForDoctorType>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: (id: string) => getIdStart(id)
      },
      {
        title: t('appointments.patient_name'),
        dataIndex: 'patient',
        key: 'patient',
        render: (value: PatientType) => (
          <Link
            to={`/patients/${value.id}`}
          >{`${value.first_name} ${value.last_name}`}</Link>
        )
      },
      {
        title: t('appointments.date_time'),
        dataIndex: 'dates',
        key: 'dates',
        render: (value: DateType[]) => {
          const dateItem = value.find(item => item.accepted);

          if (dateItem) {
            return dayjs(dateItem.date).format(FULL_DATE_FORMAT);
          }

          return '-';
        }
      },
      {
        title: t('appointments.status'),
        dataIndex: 'doctor_invoice',
        key: 'doctor_invoice',
        render: value => (
          <Tag color={value ? 'success' : 'error'}>
            {t(`invoices.${value ? 'invoiced' : 'not_invoiced'}`)}
          </Tag>
        )
      },
      {
        title: t('appointments.payment'),
        dataIndex: 'admin_paid',
        key: 'admin_paid',
        render: (value: boolean) => (
          <Tag color={value ? 'success' : 'error'}>
            {value ? t('appointments.paid') : t('appointments.not_paid')}
          </Tag>
        )
      },
      {
        title: '',
        dataIndex: 'doctor_invoice',
        key: 'actions',
        width: '5%',
        align: 'right',
        render(value, record) {
          if (value) {
            return <DownloadInvoice id={record.id} file={value.file} />;
          }

          return <CreateInvoiceModal appointmentId={record.id} />;
        }
      }
    ],
    [t]
  );

  const appointments = data?.appointmentsForDoctor?.data.results || [];
  const total = data?.appointmentsForDoctor?.data.total;

  if (!tab) {
    return null;
  }

  return (
    <PageWrapper
      title={t('invoices.invoices')}
      color="white"
      extra={[
        <HowItWorksLink
          key="how-it-works"
          type={DOCTOR_HOW_IT_WORKS_PAGES.INVOICES.key}
          title={t('common.how_it_works')}
        />
      ]}
    >
      <Tabs
        destroyInactiveTabPane
        onChange={onTabChange}
        activeKey={tab}
        items={[
          {
            label: t('common.all'),
            key: TabsValues.all
          },
          {
            label: t('invoices.not_invoiced'),
            key: TabsValues.notInvoiced
          },
          {
            label: t('invoices.invoiced'),
            key: TabsValues.invoiced
          }
        ]}
      />
      <Table
        dataSource={appointments}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total: total || 0,
          current: page + 1,
          onChange: p => {
            searchParams.set('page', `${p - 1}`);
            setSearchParams(searchParams);
          }
        }}
        scroll={{ x: 768 }}
      />
    </PageWrapper>
  );
};

export default Invoices;
